import { useEffect } from "react";
import { Outlet, useParams } from "react-router";
import { setReservationToken } from "@/lib/reservationToken";

// アンマウント時のクリアを次のタスクへ遅延させるためのタイマー ID (モジュール共有)。
// StrictMode の同期的な setup → cleanup → setup では、cleanup で予約したクリアを
// 直後の setup がキャンセルするため、トークンは消えない。実アンマウント (予約フロー離脱)
// では次のマウントが来ないためタイマーが発火してクリアされる。
let pendingClearTimer: ReturnType<typeof setTimeout> | undefined;

/**
 * 予約系画面 (/reservations/:reservationToken/...) 用のレイアウト。
 *
 * 決済成功後の success_url (§7.1) で遷移するため案件アクセストークンを持たず、URL の予約
 * アクセストークンだけが GetReservation の認可の根拠になる。子の useQuery が発火する前に
 * interceptor が最新のトークンを参照できるよう、レンダー時点で同期的に反映する (AppLayout の
 * caseToken と同方式。useEffect だと 1 tick 遅れのレースになる)。
 *
 * アンマウント時は予約アクセストークンをクリアし、reservationTokenInterceptor が予約フロー
 * 離脱後の RPC にまで `x-reservation-token` を付与し続けない (漏洩しない) ようにする。ただし
 * クリアは次のタスクへ遅延させる。StrictMode の effect 二重実行 (setup → cleanup → setup) で
 * cleanup だけが走ってトークンが失われ GetReservation が NotFound になるのを防ぐためで、
 * 再マウント時の setup が遅延クリアをキャンセルする。
 * Header + Footer は親の AppLayout が付与する。
 */
export function ReservationTokenLayout() {
  const { reservationToken } = useParams<{ reservationToken: string }>();
  setReservationToken(reservationToken);

  useEffect(() => {
    // 直前の cleanup が予約した遅延クリア (StrictMode の再マウント含む) をキャンセルし、
    // レンダー時に反映したトークンが消えないようにする。
    if (pendingClearTimer !== undefined) {
      clearTimeout(pendingClearTimer);
      pendingClearTimer = undefined;
    }
    setReservationToken(reservationToken);

    return () => {
      pendingClearTimer = setTimeout(() => {
        setReservationToken(undefined);
        pendingClearTimer = undefined;
      }, 0);
    };
  }, [reservationToken]);

  return <Outlet />;
}
